import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from './AuthContext.js';
import { useLanguage } from './LanguageContext.js';
import { storeAPI } from '../api/storeAPI.js';
import {
    getStoreModeLabel,
    STORE_MODES,
} from '../constants/storeModes.js';
import { profileAPI } from '../api/api.js';
import {
    clearRequireStorePick,
    shouldRequireStorePick,
    WORKING_IN_STORE_TOAST_KEY,
} from '../Utils/storePickSession.js';
import { usesPersistentAuthStorage } from '../Utils/authToken.js';

const STORAGE_KEY = 'selectedStoreId';
const MODE_STORAGE_KEY = 'selectedStoreMode';

const StoreContext = createContext(null);

function readStoredStoreId() {
    return (
        localStorage.getItem(STORAGE_KEY) ||
        sessionStorage.getItem(STORAGE_KEY) ||
        null
    );
}

function writeStoredStoreId(id) {
    const useLocal = usesPersistentAuthStorage();
    const storage = useLocal ? localStorage : sessionStorage;
    storage.setItem(STORAGE_KEY, id);
}

function clearStoredStoreId() {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
}

function writeStoredMode(modeId) {
    const useLocal = usesPersistentAuthStorage();
    const storage = useLocal ? localStorage : sessionStorage;
    if (modeId) {
        storage.setItem(MODE_STORAGE_KEY, modeId);
    }
}

function clearStoredMode() {
    localStorage.removeItem(MODE_STORAGE_KEY);
    sessionStorage.removeItem(MODE_STORAGE_KEY);
}

export const useStoreMode = () => {
    const context = useContext(StoreContext);
    if (!context) {
        throw new Error('useStoreMode must be used within a StoreProvider');
    }
    return context;
};

export const StoreProvider = ({ children }) => {
    const { user, isAuthenticated, loading: authLoading, updateUser } = useAuth();
    const { t, language } = useLanguage();
    const [stores, setStores] = useState([]);
    const [archivedStores, setArchivedStores] = useState([]);
    const [activeStore, setActiveStore] = useState(null);
    const [ready, setReady] = useState(false);
    const [loadingStores, setLoadingStores] = useState(false);
    const [loadingArchivedStores, setLoadingArchivedStores] = useState(false);

    const refreshProfilePermissions = useCallback(async () => {
        try {
            const profileRes = await profileAPI.getProfile();
            if (profileRes.data?.admin) {
                updateUser(profileRes.data.admin);
            }
        } catch {
            /* keep current user */
        }
    }, [updateUser]);

    const applyActiveStore = useCallback((store) => {
        if (!store) {
            setActiveStore(null);
            clearStoredStoreId();
            clearStoredMode();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('quazbit-active-store-changed', { detail: { storeId: null } }));
            }
            return;
        }
        setActiveStore(store);
        writeStoredStoreId(store.id || store._id);
        writeStoredMode(store.type);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('quazbit-active-store-changed', {
                detail: { storeId: String(store.id || store._id) },
            }));
        }
    }, []);

    const loadStores = useCallback(async () => {
        if (!isAuthenticated) {
            setStores([]);
            setArchivedStores([]);
            applyActiveStore(null);
            setReady(true);
            return;
        }

        setLoadingStores(true);
        try {
            const { data } = await storeAPI.list();
            const list = Array.isArray(data.stores) ? data.stores : [];
            setStores(list);

            const storedId = readStoredStoreId();
            const serverActiveId = data.activeStoreId
                ? String(data.activeStoreId)
                : null;
            const requirePick = shouldRequireStorePick();

            const byId = (id) =>
                list.find((s) => String(s.id || s._id) === String(id));

            let preferred = null;

            if (list.length === 1) {
                preferred = list[0];
                if (requirePick) {
                    try {
                        sessionStorage.setItem(
                            WORKING_IN_STORE_TOAST_KEY,
                            preferred.name || '',
                        );
                    } catch {
                        /* ignore storage errors */
                    }
                }
                clearRequireStorePick();
            } else if (list.length > 1 && requirePick) {
                // Fresh login: ask which store — do not auto-select
                preferred = null;
            } else {
                preferred = byId(storedId) || byId(serverActiveId) || null;
            }

            if (preferred) {
                applyActiveStore(preferred);
                const preferredId = String(preferred.id || preferred._id);
                if (serverActiveId !== preferredId) {
                    try {
                        await storeAPI.select(preferredId);
                    } catch {
                        /* local selection still applied */
                    }
                }
            } else {
                applyActiveStore(null);
            }
        } catch (error) {
            console.error('Failed to load stores:', error);
            setStores([]);
            applyActiveStore(null);
        } finally {
            setLoadingStores(false);
            setReady(true);
        }
    }, [isAuthenticated, applyActiveStore]);

    useEffect(() => {
        if (authLoading) return;
        if (!isAuthenticated) {
            setStores([]);
            applyActiveStore(null);
            setReady(true);
            return;
        }
        setReady(false);
        loadStores();
    }, [authLoading, isAuthenticated, user?._id, loadStores, applyActiveStore]);

    const setStoreMode = useCallback((modeId) => {
        const match = stores.find((s) => s.type === modeId);
        if (match) {
            applyActiveStore(match);
        } else {
            writeStoredMode(modeId);
        }
    }, [stores, applyActiveStore]);

    const selectStore = useCallback(async (storeOrId) => {
        const id =
            typeof storeOrId === 'string'
                ? storeOrId
                : storeOrId?.id || storeOrId?._id;
        if (!id) return null;

        const local = stores.find((s) => String(s.id || s._id) === String(id));
        if (local) {
            applyActiveStore(local);
        }

        const { data } = await storeAPI.select(id);
        const store = data.store || local;
        if (store) {
            applyActiveStore(store);
        }
        clearRequireStorePick();
        return store;
    }, [stores, applyActiveStore]);

    const createStore = useCallback(async ({ name, type }) => {
        // The dashboard language seeds the store's regional defaults (e.g. ar → SAR, Asia/Riyadh).
        const { data } = await storeAPI.create({ name, type, primaryLanguage: language });
        const store = data.store;
        setStores((prev) => {
            const id = String(store.id || store._id);
            if (prev.some((s) => String(s.id || s._id) === id)) return prev;
            return [...prev, store];
        });
        applyActiveStore(store);
        clearRequireStorePick();
        if (data.admin) {
            updateUser(data.admin);
        } else {
            await refreshProfilePermissions();
        }
        return store;
    }, [applyActiveStore, language, refreshProfilePermissions, updateUser]);

    const renameStore = useCallback(async (storeOrId, name) => {
        const id =
            typeof storeOrId === 'string'
                ? storeOrId
                : storeOrId?.id || storeOrId?._id;
        if (!id) return null;

        const { data } = await storeAPI.rename(id, { name });
        const store = data.store;
        if (!store) return null;

        setStores((prev) =>
            prev.map((s) =>
                String(s.id || s._id) === String(store.id || store._id) ? store : s,
            ),
        );

        const activeId = activeStore?.id || activeStore?._id;
        if (activeId && String(activeId) === String(store.id || store._id)) {
            applyActiveStore(store);
        }

        return store;
    }, [activeStore, applyActiveStore]);

    const deleteStore = useCallback(async (storeOrId, confirmName) => {
        const id =
            typeof storeOrId === 'string'
                ? storeOrId
                : storeOrId?.id || storeOrId?._id;
        if (!id) return null;

        const { data } = await storeAPI.delete(id, { confirmName });
        const deletedId = String(data.store?.id || data.store?._id || id);

        setStores((prev) =>
            prev.filter((s) => String(s.id || s._id) !== deletedId),
        );

        if (data.store) {
            setArchivedStores((prev) => {
                if (prev.some((s) => String(s.id || s._id) === deletedId)) {
                    return prev.map((s) =>
                        String(s.id || s._id) === deletedId ? data.store : s,
                    );
                }
                return [data.store, ...prev];
            });
        }

        const activeId = activeStore?.id || activeStore?._id;
        if (activeId && String(activeId) === deletedId) {
            if (data.fallbackStore) {
                applyActiveStore(data.fallbackStore);
                try {
                    await storeAPI.select(data.fallbackStore.id || data.fallbackStore._id);
                } catch {
                    /* local selection still applied */
                }
            } else {
                applyActiveStore(null);
            }
        }

        return data;
    }, [activeStore, applyActiveStore]);

    const purgeStoreForever = useCallback(async (storeOrId, confirmName) => {
        const id =
            typeof storeOrId === 'string'
                ? storeOrId
                : storeOrId?.id || storeOrId?._id;
        if (!id) return null;

        const { data } = await storeAPI.purgeForever(id, { confirmName });
        const deletedId = String(data.store?.id || data.store?._id || id);

        setStores((prev) =>
            prev.filter((s) => String(s.id || s._id) !== deletedId),
        );
        setArchivedStores((prev) =>
            prev.filter((s) => String(s.id || s._id) !== deletedId),
        );

        const activeId = activeStore?.id || activeStore?._id;
        if (activeId && String(activeId) === deletedId) {
            if (data.fallbackStore) {
                applyActiveStore(data.fallbackStore);
                try {
                    await storeAPI.select(data.fallbackStore.id || data.fallbackStore._id);
                } catch {
                    /* local selection still applied */
                }
            } else {
                applyActiveStore(null);
            }
        }

        return data;
    }, [activeStore, applyActiveStore]);

    const loadArchivedStores = useCallback(async () => {
        if (!isAuthenticated) {
            setArchivedStores([]);
            return [];
        }

        setLoadingArchivedStores(true);
        try {
            const { data } = await storeAPI.listArchived();
            const list = Array.isArray(data.stores) ? data.stores : [];
            setArchivedStores(list);
            return list;
        } catch (err) {
            setArchivedStores([]);
            throw err;
        } finally {
            setLoadingArchivedStores(false);
        }
    }, [isAuthenticated]);

    const restoreStore = useCallback(async (storeOrId) => {
        const id =
            typeof storeOrId === 'string'
                ? storeOrId
                : storeOrId?.id || storeOrId?._id;
        if (!id) return null;

        const { data } = await storeAPI.restore(id);
        const store = data.store;
        if (!store) return null;

        const restoredId = String(store.id || store._id);
        setArchivedStores((prev) =>
            prev.filter((s) => String(s.id || s._id) !== restoredId),
        );
        setStores((prev) => {
            if (prev.some((s) => String(s.id || s._id) === restoredId)) {
                return prev.map((s) =>
                    String(s.id || s._id) === restoredId ? store : s,
                );
            }
            return [...prev, store];
        });

        return store;
    }, []);

    const clearStoreMode = useCallback(() => {
        applyActiveStore(null);
    }, [applyActiveStore]);

    const storeMode = activeStore?.type || null;
    const needsStoreSelection =
        isAuthenticated && !activeStore && (stores.length === 0 || stores.length > 1);


    const availableModes = useMemo(() => {
        const types = [...new Set(stores.map((s) => s.type))];
        return STORE_MODES.filter((mode) => types.includes(mode.id));
    }, [stores]);

    const value = useMemo(
        () => ({
            storeMode,
            storeModeLabel: activeStore
                ? activeStore.name
                : getStoreModeLabel(storeMode, t),
            availableModes,
            stores,
            archivedStores,
            activeStore,
            needsStoreSelection,
            ready: !authLoading && ready,
            loadingStores,
            loadingArchivedStores,
            setStoreMode,
            selectStore,
            createStore,
            renameStore,
            deleteStore,
            purgeStoreForever,
            restoreStore,
            refreshStores: loadStores,
            refreshArchivedStores: loadArchivedStores,
            clearStoreMode,
        }),
        [
            storeMode,
            activeStore,
            availableModes,
            stores,
            archivedStores,
            needsStoreSelection,
            authLoading,
            ready,
            loadingStores,
            loadingArchivedStores,
            setStoreMode,
            selectStore,
            createStore,
            renameStore,
            deleteStore,
            purgeStoreForever,
            restoreStore,
            loadStores,
            loadArchivedStores,
            clearStoreMode,
            t,
        ]
    );

    return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

StoreProvider.propTypes = {
    children: PropTypes.node,
};
